import React from "react";
import styled from "styled-components";
import { Text, types, Image, useAdminContext } from "react-bricks/frontend";
import { color } from "styles/colours";
import { H2 } from "components/atoms/Heading";
import { P1 } from "components/atoms/Paragraph";
import { Link } from "components/atoms/Link";
import { mobile } from "components/templates/Breakpoints";
import TextBlock from "../common/TextBlock";
import { fonts } from "@styles";

export interface InfoCardProps {
  title: string;
  content: string;
  height: number;
  width: number;
  hasLink: boolean;
  linkText: string;
  linkUrl: string;
  imageType: "icon" | "cover";
}

export const InfoCard: types.Brick<InfoCardProps> = ({
  height,
  width,
  hasLink,
  linkUrl,
  imageType,
}) => {
  const { isAdmin } = useAdminContext();
  const cardContent = (
    <>
      {imageType === "icon" && <Image propName="iconImage" alt={"image"} maxWidth={52} />}
      {imageType === "cover" && (
        <CoverImage>
          <Image propName="coverImage" alt={"image"} maxWidth={350} />
        </CoverImage>
      )}
      <Text
        propName="title"
        placeholder="Title"
        renderBlock={({ children }) => <Title>{children}</Title>}
      />
      <Content>
        <TextBlock
          propName="content"
          placeholder="This is where the content for the Product Card lives..."
          fontSize="1rem"
          lineHeight="1.2rem"
        />
      </Content>
      {hasLink && (
        <LinkWrapper>
          <Link href={linkUrl}>
            <Text
              propName="linkText"
              placeholder="Learn more"
              renderBlock={({ children }) => <>{children}</>}
            />
          </Link>
        </LinkWrapper>
      )}
    </>
  );

  return (
    <>
      {hasLink && linkUrl && !isAdmin ? (
        <Link href={linkUrl} passHref>
          <Container height={height} width={width} hasLink={hasLink}>
            {cardContent}
          </Container>
        </Link>
      ) : (
        <Container height={height} width={width} hasLink={hasLink}>
          {cardContent}
        </Container>
      )}
    </>
  );
};

InfoCard.schema = {
  name: "info-card",
  label: "Info Card",
  hideFromAddMenu: true,
  // Define repeaters for child bricks
  repeaterItems: [],
  // Define default props
  getDefaultProps: () => ({
    title: "Group Health Insurance",
    content:
      "Covers the cost of high-quality healthcare, quickly, if employees were to get ill. Provided by Bupa.",
    height: 22,
    width: 25,
    hasLink: false,
    linkText: "Learn more",
    imageType: "icon",
    linkUrl: "#",
    verticalPadding: 3,
    horizontalPadding: 2,
    iconImage: {
      src: "/static/icons/Health.svg",
    },
  }),
  // Define side edit props
  sideEditProps: [
    {
      name: "height",
      label: "Height",
      type: types.SideEditPropType.Number,
      shouldRefreshStyles: true,
    },
    {
      name: "width",
      label: "Weight",
      type: types.SideEditPropType.Number,
      shouldRefreshStyles: true,
    },
    {
      name: "hasLink",
      label: "Show Link?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "linkText",
      label: "Link Text",
      type: types.SideEditPropType.Text,
      show: (props) => props.hasLink,
    },
    {
      name: "linkUrl",
      label: "Link URL",
      type: types.SideEditPropType.Text,
      show: (props) => props.hasLink,
    },
  ],
};

const CoverImage = styled.div`
  &::before {
    content: "";
    display: block;
    height: 200px;
    width: 100%;
    margin-top: -3rem;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 200px;
  }
`;

const Container = styled.div<{
  height: number;
  width: number;
  hasLink: boolean;
}>`
  position: relative;
  border: 0.125rem solid #d9d9d7;
  box-shadow: #f5f5f5 0.5rem 0.5rem 0px;
  border-radius: 0.5rem;
  padding: 3rem 2rem;
  min-height: ${(p) => p.height}rem;
  width: ${(p) => p.width}rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: 200ms ease-out;

  &:hover {
    ${(p) =>
      p.hasLink &&
      `
      transform: translateY(-1rem);
      box-shadow: 0.9rem 0.9rem 0px ${color.B220};
    `}
  }

  ${mobile} {
    height: auto;
  }

  @media only screen and (max-width: 25rem) {
    width: 100%;
  }
`;

const Title = styled(H2)<{}>`
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 2rem;
  text-align: center;
  width: 100%;
  font-family: ${fonts.body};
`;

const Content = styled(P1)`
  width: 100%;
  padding: 0;
  font-size: 1rem;
  text-align: center;
`;

const LinkWrapper = styled.div`
  font-size: 1rem;
  color: ${color.P100};
  text-decoration-line: underline;
`;
